import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bot, ShieldBan, ShieldOff } from "lucide-react";
import { Button } from "@ui/components/ui/button";
import { Badge } from "@ui/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@ui/components/ui/card";
import {
  useListSystemBotListings,
  useDelistSystemBotListing,
  useEmergencyDisableSystemBotListing,
} from "@tubo/api-models/hooks/system-bot-api.hooks";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings/bots")({
  component: SystemBotsPage,
});

function getStatusBadge(status: string, t: ReturnType<typeof useTranslation<"platformManagement">>["t"]) {
  if (status === "published") return <Badge variant="default">{t("systemBots.status.published")}</Badge>;
  if (status === "delisted") return <Badge variant="secondary">{t("systemBots.status.delisted")}</Badge>;
  if (status === "rejected") return <Badge variant="destructive">{t("systemBots.status.rejected")}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function SystemBotsPage() {
  const { t } = useTranslation("platformManagement");
  const queryClient = useQueryClient();
  const { data: listings, isLoading } = useListSystemBotListings();
  const delistMutation = useDelistSystemBotListing({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["system-bot-listings"] });
        toast.success(t("systemBots.delisted"));
      },
      onError: () => toast.error(t("systemBots.delistFailed")),
    },
  });
  const disableMutation = useEmergencyDisableSystemBotListing();

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-8">
      <header className="mb-8">
        <div className="mb-3 flex items-center gap-2 text-primary">
          <Bot className="size-5" />
          <span className="text-sm font-medium">{t("systemBots.title")}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("systemBots.marketTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("systemBots.description")}</p>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">{t("systemBots.loading")}</p>}

      {!isLoading && (!listings?.listings || listings.listings.length === 0) && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Bot className="mb-3 size-10 text-muted-foreground" />
            <p className="font-medium">{t("systemBots.empty")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("systemBots.emptyDescription")}</p>
          </CardContent>
        </Card>
      )}

      {listings?.listings && listings.listings.length > 0 && (
        <div className="space-y-3">
          {listings.listings?.map((listing) => (
            <Card key={listing.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                    <Bot className="size-5 text-muted-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{listing.name}</CardTitle>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      v{listing.version} · {listing.botType === "agent" ? t("systemBots.botType.agent") : t("systemBots.botType.chat")}
                      {listing.category ? ` · ${listing.category}` : ""}
                    </p>
                  </div>
                </div>
                {getStatusBadge(listing.status, t)}
              </CardHeader>
              <CardContent>
                {listing.desc && <p className="text-sm text-muted-foreground">{listing.desc}</p>}
                {listing.releaseNotes && (
                  <p className="mt-2 text-xs text-muted-foreground">{t("systemBots.releaseNotes", { notes: listing.releaseNotes })}</p>
                )}
                <div className="mt-4 flex gap-2">
                  {listing.status === "published" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={delistMutation.isPending}
                      onClick={() => {
                        delistMutation.mutate({ id: listing.id });
                      }}
                    >
                      <ShieldOff className="size-4" />
                      {t("systemBots.delist")}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={disableMutation.isPending}
                    onClick={() => {
                      disableMutation.mutate({ id: listing.id }, {
                        onSuccess: (disabledCount) => {
                          toast.success(t("systemBots.disabledInstallations", { count: disabledCount }));
                        },
                        onError: () => toast.error(t("systemBots.operationFailed")),
                      });
                    }}
                  >
                    <ShieldBan className="size-4" />
                    {t("systemBots.disableInstallations")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
